/**
 * Contador de usuários online por módulo (visto só pela intranet).
 *
 * Fica de propósito FORA do ERP: manda um beacon para uma Lambda própria e não passa pelo
 * `$api`/axios — o PDV offline decide se a internet caiu pelos eventos do `api.js`, e uma
 * Lambda lenta não pode influenciar isso. Se o beacon falhar (Lambda fora, rede corporativa,
 * bloqueador, PDV sem internet), nada acontece: não mostra nada, não tenta de novo.
 *
 * Sem duplicação:
 *  - cada aba só anota no localStorage o módulo em que está (e as medições dela);
 *  - um beacon por navegador a cada 5 min, levando os módulos de todas as abas visíveis;
 *    o Web Locks API garante que duas abas não mandem ao mesmo tempo;
 *  - trocar de módulo recarrega a página (cada módulo é um build), mas o horário do último
 *    beacon fica no localStorage, então o reload não gera beacon a mais;
 *  - no servidor o registro é empresa:usuario, então F5/outro navegador não somam.
 *
 * Desligado quando: sem VUE_APP_CONTADOR_ONLINE_URL no build, sem usuário logado, usuário de
 * suporte (Betel acessando o cliente), tela servida pelo app do PDV na rede da loja.
 */
const URL_CONTADOR = process.env.VUE_APP_CONTADOR_ONLINE_URL || ''
const VERSAO = (process.env.VUE_APP_DEPLOY_TIME || '').replace(/[^A-Za-z0-9_.-]/g, '').slice(0, 40)

export const INTERVALO = 300000      // um beacon por navegador a cada 5 min
const BATIDA = 30000                 // cada aba atualiza a própria anotação a cada 30 s
// Uma chave por aba (contador_online_aba_<id>): com um objeto só, duas abas gravando no
// mesmo instante apagavam a anotação uma da outra.
const PREFIXO_ABA = 'contador_online_aba_'
const CHAVE_ULTIMO = 'contador_online_ultimo'
const LIMITE_LENTA = 3000            // chamada de API acima disso conta como lenta
const MAX_CARGAS = 20
const MAX_DETALHES = 10              // por aba e por tipo (lentas/erros) entre dois beacons
const TAMANHO_BEACON = 3800          // a Lambda recusa acima de 4 KB: detalhe sai antes de passar

/**
 * Endpoint sem nada que identifique alguém: só host + caminho, sem query string, e
 * segmento que parece id (número, uuid, hash) vira `:id`.
 */
export const normalizaEndpoint = (endereco) => {
    try {
        const url = new URL(endereco, window.location.href)
        const caminho = url.pathname.split('/').map((parte) => {
            if (/^\d+$/.test(parte) || /^[0-9a-f-]{16,}$/i.test(parte) || /^[A-Za-z0-9_=-]{24,}$/.test(parte)) {
                return ':id'
            }
            return parte
        }).join('/')
        return (url.host + caminho).replace(/[^A-Za-z0-9_/:.-]/g, '').slice(0, 160)
    } catch (erro) {
        return ''
    }
}

/** Mensagem de erro sem dado pessoal provável: e-mail e sequência de 4+ dígitos saem. */
export const higienizaMensagem = (texto) => String(texto || '')
    .replace(/[\w.+-]+@[\w-]+\.[\w.-]+/g, '[email]')
    .replace(/\d{4,}/g, '#')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 160)

const arquivoDoErro = (arquivo, linha, coluna) => {
    const nome = String(arquivo || '').split('?')[0].split('/').pop()
    return nome ? (nome + ':' + (linha || 0) + ':' + (coluna || 0)).slice(0, 80) : ''
}

let iniciado = false

const le = (chave, padrao) => {
    try {
        const valor = localStorage.getItem(chave)
        return valor ? JSON.parse(valor) : padrao
    } catch (erro) {
        return padrao
    }
}

const grava = (chave, valor) => {
    try {
        localStorage.setItem(chave, JSON.stringify(valor))
    } catch (erro) {
        // localStorage cheio/bloqueado: o contador só deixa de contar
    }
}

const apaga = (chave) => {
    try {
        localStorage.removeItem(chave)
    } catch (erro) {
        // bloqueado
    }
}

/** Anotações de todas as abas deste navegador: [[chave, anotação], ...] */
const abasAnotadas = () => {
    const lista = []
    try {
        for (let i = 0; i < localStorage.length; i++) {
            const chave = localStorage.key(i)
            if (chave && chave.indexOf(PREFIXO_ABA) === 0) {
                lista.push([chave, le(chave, null)])
            }
        }
    } catch (erro) {
        // bloqueado
    }
    return lista
}

/**
 * Mesma regra do `formataUrlBase` (roteamento.js): submódulo primeiro, depois módulo;
 * fora dos módulos é o ERP base. Devolve a chave completa (`pedidos:vendas`), porque o
 * `$url.module` guarda só o submódulo e `vendas` sozinho seria ambíguo.
 */
export const moduloDaUrl = (caminho, modulos) => {
    const partes = (caminho || '').split('?')[0].split('/')
    const lista = Array.isArray(modulos) ? modulos : []
    const submodulo = lista.find(item => {
        if (!item.includes(':')) {
            return false
        }
        const [pai, filho] = item.split(':')
        return partes[1] == pai && partes[2] == filho
    })
    if (submodulo) {
        return submodulo
    }
    return lista.find(item => !item.includes(':') && partes[1] == item) || 'erp'
}

const tipoCliente = () => {
    try {
        if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) {
            return 'pwa'
        }
    } catch (erro) {
        // sem matchMedia
    }
    return /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent) ? 'mobile' : 'navegador'
}

const navegador = () => {
    const ua = navigator.userAgent
    if (/Edg\//.test(ua)) return 'edge'
    if (/OPR\//.test(ua)) return 'opera'
    if (/Firefox\//.test(ua)) return 'firefox'
    if (/Chrome\//.test(ua)) return 'chrome'
    if (/Safari\//.test(ua)) return 'safari'
    return 'outro'
}

const desligado = (usuario) => {
    if (!URL_CONTADOR || typeof navigator === 'undefined' || !navigator.sendBeacon) {
        return true
    }
    try {
        if (window.__PDV_SERVIDOR__) {
            return true
        }
    } catch (erro) {
        return true
    }
    return !usuario || !usuario.id || !usuario.Empresa || !usuario.Empresa.id || !!usuario.Suporte
}

/**
 * @param {Function} pegaUsuario  devolve o usuário logado (store)
 * @param {Function} pegaRota     devolve o padrão da rota atual (`/estoque/produtos/editar/:id`),
 *                                nunca a URL com ids
 * @param {Object}   [opcoes]     só para teste: { modulos, url, agora }
 */
export const iniciaContadorOnline = (pegaUsuario, pegaRota, opcoes = {}) => {
    if (iniciado) {
        return
    }
    const url = opcoes.url || URL_CONTADOR
    if (!url && !opcoes.url) {
        return
    }
    iniciado = true
    const agora = opcoes.agora || (() => Date.now())
    let modulos = opcoes.modulos
    if (!modulos) {
        try {
            modulos = require('@/modules/modules.json')
        } catch (erro) {
            modulos = []
        }
    }

    const idAba = Math.random().toString(36).slice(2, 10)
    const medicoes = { cargas: [], lentas: 0, erros: 0, lentasDet: [], errosDet: [] }
    const anotaErro = (mensagem, arquivo) => {
        medicoes.erros++
        if (medicoes.errosDet.length < MAX_DETALHES) {
            medicoes.errosDet.push({ m: higienizaMensagem(mensagem) || 'erro sem mensagem', a: arquivo || '' })
        }
    }

    // --- medições passivas (só observam; não trocam handler nenhum) ---------------------
    try {
        const observador = new PerformanceObserver((lista) => {
            lista.getEntries().forEach((entrada) => {
                if (entrada.entryType == 'navigation') {
                    const carga = Math.round(entrada.loadEventEnd || entrada.duration || 0)
                    if (carga > 0 && medicoes.cargas.length < MAX_CARGAS) {
                        medicoes.cargas.push(carga)
                    }
                } else if (
                    (entrada.initiatorType == 'xmlhttprequest' || entrada.initiatorType == 'fetch') &&
                    entrada.duration > LIMITE_LENTA &&
                    entrada.name.indexOf(url) !== 0
                ) {
                    medicoes.lentas++
                    const endpoint = normalizaEndpoint(entrada.name)
                    if (endpoint && medicoes.lentasDet.length < MAX_DETALHES) {
                        medicoes.lentasDet.push({ e: endpoint, ms: Math.round(entrada.duration) })
                    }
                }
            })
        })
        observador.observe({ type: 'navigation', buffered: true })
        observador.observe({ type: 'resource', buffered: false })
    } catch (erro) {
        // navegador sem PerformanceObserver: fica sem medição
    }
    window.addEventListener('error', (evento) => {
        try {
            anotaErro(evento.message, arquivoDoErro(evento.filename, evento.lineno, evento.colno))
        } catch (erro) {
            // contador é acessório
        }
    })
    window.addEventListener('unhandledrejection', (evento) => {
        try {
            const motivo = evento.reason
            anotaErro('Promise: ' + ((motivo && motivo.message) || String(motivo)), '')
        } catch (erro) {
            // contador é acessório
        }
    })

    // --- anotação desta aba ---------------------------------------------------------------
    const anota = () => {
        const usuario = pegaUsuario()
        if (desligado(usuario) || document.hidden) {
            return
        }
        const momento = agora()
        const chave = PREFIXO_ABA + idAba
        // limpa anotação de aba fechada há tempo (crash, aba morta)
        abasAnotadas().forEach(([outra, aba]) => {
            if (outra !== chave && (!aba || momento - aba.visto > INTERVALO * 2)) {
                apaga(outra)
            }
        })
        const anterior = le(chave, {}) || {}
        let rota = ''
        try {
            rota = (pegaRota() || '').slice(0, 120)
        } catch (erro) {
            rota = ''
        }
        grava(chave, {
            usuario: usuario.id,
            empresa: usuario.Empresa.id,
            modulo: moduloDaUrl(window.location.pathname, modulos),
            rota,
            visto: momento,
            cargas: (anterior.cargas || []).concat(medicoes.cargas).slice(0, MAX_CARGAS),
            lentas: (anterior.lentas || 0) + medicoes.lentas,
            erros: (anterior.erros || 0) + medicoes.erros,
            lentasDet: (anterior.lentasDet || []).concat(medicoes.lentasDet).slice(0, MAX_DETALHES),
            errosDet: (anterior.errosDet || []).concat(medicoes.errosDet).slice(0, MAX_DETALHES),
        })
        medicoes.cargas = []
        medicoes.lentas = 0
        medicoes.erros = 0
        medicoes.lentasDet = []
        medicoes.errosDet = []
    }

    // --- beacon (no máximo um por navegador a cada INTERVALO) -----------------------------
    const tentaEnviar = () => {
        const usuario = pegaUsuario()
        if (desligado(usuario) || document.hidden) {
            return
        }
        if (agora() - (le(CHAVE_ULTIMO, 0) || 0) < INTERVALO) {
            return
        }
        const momento = agora()
        grava(CHAVE_ULTIMO, momento)
        const doUsuario = abasAnotadas()
            .filter(([, aba]) => aba && aba.usuario == usuario.id && momento - aba.visto <= INTERVALO + BATIDA)
        if (!doUsuario.length) {
            return
        }
        const dados = {
            empresa_id: Number(usuario.Empresa.id),
            usuario_id: Number(usuario.id),
            versao: VERSAO || undefined,
            cliente: tipoCliente(),
            navegador: navegador(),
            abas: doUsuario.slice(0, 10).map(([, aba]) => ({
                modulo: aba.modulo,
                rota: aba.rota || undefined,
                cargas: aba.cargas && aba.cargas.length ? aba.cargas : undefined,
                lentas: aba.lentas || undefined,
                erros: aba.erros || undefined,
                lentas_det: aba.lentasDet && aba.lentasDet.length ? aba.lentasDet : undefined,
                erros_det: aba.errosDet && aba.errosDet.length ? aba.errosDet : undefined,
            })),
        }
        // Cabe no limite da Lambda: tira detalhe (o mais recente primeiro) até caber; as
        // contagens nunca saem.
        let corpo = JSON.stringify(dados)
        for (let tentativa = 0; corpo.length > TAMANHO_BEACON && tentativa < 200; tentativa++) {
            const comDetalhe = dados.abas.find(aba => (aba.erros_det && aba.erros_det.length) || (aba.lentas_det && aba.lentas_det.length))
            if (!comDetalhe) {
                break
            }
            const lista = comDetalhe.erros_det && comDetalhe.erros_det.length ? comDetalhe.erros_det : comDetalhe.lentas_det
            lista.pop()
            corpo = JSON.stringify(dados)
        }
        // medições já enviadas não vão de novo
        doUsuario.forEach(([chave, aba]) => {
            grava(chave, Object.assign({}, aba, { cargas: [], lentas: 0, erros: 0, lentasDet: [], errosDet: [] }))
        })
        try {
            // text/plain = "simple request": sem preflight de CORS e sem o token do ERP (o domínio da Lambda não tem cookie)
            navigator.sendBeacon(url + '/ping', new Blob([corpo], { type: 'text/plain' }))
        } catch (erro) {
            // falha silenciosa
        }
    }

    // Nenhum erro daqui pode escapar para a tela, para o console do cliente ou para o
    // contador de erros de JS (que mede o ERP, não este plugin).
    const semErro = (funcao) => (...argumentos) => {
        try {
            return funcao(...argumentos)
        } catch (erro) {
            // contador é acessório: falhou, ignora
        }
    }

    const ciclo = semErro(() => {
        anota()
        if (navigator.locks && navigator.locks.request) {
            navigator.locks.request('contador_online', { ifAvailable: true }, semErro((trava) => {
                if (trava) {
                    tentaEnviar()
                }
            })).catch(() => {})
        } else {
            tentaEnviar()
        }
    })

    // primeira anotação depois que a tela assenta (o usuário logado chega depois do boot)
    setTimeout(ciclo, 5000)
    setInterval(ciclo, BATIDA)
    document.addEventListener('visibilitychange', () => {
        if (!document.hidden) {
            ciclo()
        }
    })
}

// só para teste
export const _reinicia = () => { iniciado = false }
